import { RouterInput } from '@gongsegae/api';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useSyncCartPrices } from '@/hooks/useSyncCartPrices';
import { useSyncCartStock } from '@/hooks/useSyncCartStock';
import { useValidatePricesOnSubmit } from '@/hooks/useValidatePricesOnSubmit';
import type { PriceChange } from '@/lib/cart-utils';
import { saveCustomerInfo } from '@/lib/localStorage';
import { trpc } from '@/lib/trpc';
import { useCartStore } from '@/stores/cart-store';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';
import { useReservationResultStore } from '@/stores/reservation-result-store';
import { useReservationStore } from '@/stores/reservation-store';

import CartHeader from '../features/cart/components/CartHeader';
import CartItemList from '../features/cart/components/CartItemList';
import DeleteConfirmDialog, { type DeleteConfirmDialogRef } from '../features/cart/components/DeleteConfirmDialog';
import PriceChangeDialog, { type PriceChangeDialogRef } from '../features/cart/components/PriceChangeDialog';
import ReservationButton from '../features/cart/components/ReservationButton';
import ReservationForm from '../features/cart/components/ReservationForm';
import TermsChecklist from '../features/cart/components/TermsChecklist';

import type { SelectedOption } from '@/types/cart';

type OrderInsert = RouterInput['order']['create']['order'];
type OrderItemInsert = RouterInput['order']['create']['items'][number];

const isPriceChange = (value: unknown): value is PriceChange => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'name' in value &&
    'oldPrice' in value &&
    'newPrice' in value &&
    typeof (value as PriceChange).id === 'string' &&
    typeof (value as PriceChange).name === 'string' &&
    typeof (value as PriceChange).oldPrice === 'number' &&
    typeof (value as PriceChange).newPrice === 'number'
  );
};

const getPriceChangesFromError = (error: unknown): PriceChange[] | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: { reason?: unknown; priceChanges?: unknown } }).data;
  if (data?.reason !== 'PRICE_CHANGED' || !Array.isArray(data.priceChanges)) {
    return null;
  }

  return data.priceChanges.filter(isPriceChange);
};

const getSelectedOptionsAdditionalPrice = (selectedOptions: SelectedOption[]) => {
  return selectedOptions.reduce((sum, option) => sum + option.additional_price, 0);
};

export default function CartPage() {
  useScrollToTop();
  useSyncCartPrices();

  const navigate = useNavigate();
  const { items, updateQuantity, getTotalPrice, clearCart, removeItem } = useCartStore();
  const { pickupDate, pickupTime, clearPickupDateTime } = usePickupDateTimeStore();
  const {
    formData,
    isFormValid,
    areAllTermsChecked,
    resetReservation,
    setIdle,
    setSubmitting,
    setSuccess,
    clearError,
    isSubmitting,
    error,
    setError,
  } = useReservationStore();
  const { setReservation } = useReservationResultStore();
  const { getStockAwareLimit, validateCartStock } = useSyncCartStock(pickupDate);

  // tRPC mutation for creating an order
  const { mutateAsync: createOrder } = useMutation(trpc.order.create.mutationOptions());

  // 삭제 확인 모달 ref
  const deleteConfirmDialogRef = useRef<DeleteConfirmDialogRef>(null);
  // 가격 변경 알림 모달 ref
  const priceChangeDialogRef = useRef<PriceChangeDialogRef>(null);
  const { applyPriceUpdates } = useValidatePricesOnSubmit();

  const handleUpdateQuantity = (id: string, change: number) => {
    const item = items.find(item => item.id === id);
    if (item) {
      if (item.quantity + change < 1) {
        // 삭제 확인 모달 열기
        deleteConfirmDialogRef.current?.showDeleteConfirm(id);
        return;
      }

      const nextQuantity = item.quantity + change;
      const stockAwareLimit = getStockAwareLimit(item);
      if (change > 0 && nextQuantity > stockAwareLimit) {
        toast.error(`'${item.name}'은(는) 최대 ${stockAwareLimit}개까지 주문할 수 있습니다.`);
        return;
      }

      updateQuantity(id, nextQuantity);
    }
  };

  const getKoreanStandardDateString = (date: Date): string => {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  };

  const handleReservation = async () => {
    if (isSubmitting()) return; // 중복 클릭 방지

    // 가격/재고 검증을 포함한 전체 제출 흐름 동안 버튼을 잠급니다.
    setSubmitting();

    try {
      const stockValidation = validateCartStock();
      if (!stockValidation.isValid) {
        throw new Error('재고가 변경되었습니다. 장바구니 수량을 확인한 뒤 다시 시도해주세요.');
      }

      const totalAmount = getTotalPrice();

      // 픽업 날짜와 시간 확인
      if (!pickupDate || !pickupTime) {
        throw new Error('픽업 날짜와 시간을 선택해주세요.');
      }

      // 픽업 날짜와 시간을 ISO string으로 변환
      const [hours, minutes] = pickupTime.split(':');
      const pickupDateTime = new Date(pickupDate);
      pickupDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Validate minimum pickup date (KST): pickup is allowed starting from the next day.
      const now = new Date();
      const minimumPickupDate = getKoreanStandardDateString(new Date(now.getTime() + 24 * 60 * 60 * 1000));
      const selectedPickupDate = getKoreanStandardDateString(pickupDateTime);
      if (selectedPickupDate < minimumPickupDate) {
        throw new Error('픽업은 예약 다음 날부터 가능합니다. 픽업 날짜를 다시 선택해주세요.');
      }

      // OrderInsert 데이터 생성
      const orderData: OrderInsert = {
        customer_name: formData.name,
        customer_phone: formData.phone,
        pickup_datetime: pickupDateTime.toISOString(),
        total_amount: totalAmount,
        special_requests: formData.requirements || undefined,
        payment_status: 'pending',
      };

      // OrderItemInsert[] 데이터 생성
      const orderItems: OrderItemInsert[] = items.map(item => {
        // CartItem.id에서 menu_id 추출 (id 형식: "{menuId}-{optionId1}-{optionId2}...")
        const menuId = parseInt(item.id.split('-')[0]);

        // selected_options: CartItem.selectedOptions가 있으면 그대로 사용, 없으면 빈 배열
        const selectedOptions: SelectedOption[] = item.selectedOptions || [];
        const optionTotal = getSelectedOptionsAdditionalPrice(selectedOptions);
        const menuPrice = item.price - optionTotal;

        return {
          cart_item_id: item.id,
          menu_id: menuId,
          menu_name: item.name,
          menu_price: menuPrice,
          quantity: item.quantity,
          selected_options: selectedOptions,
          option_display_text: item.option || undefined,
          item_total: item.price * item.quantity,
        };
      });

      console.log('Order data:', orderData);
      console.log('Order items:', orderItems);

      // tRPC mutation 호출 (order_id는 서버에서 설정)
      const response = await createOrder({
        order: orderData,
        items: orderItems,
      });

      if (response.order) {
        // 성공 상태로 변경
        setSuccess();

        // Save customer info to localStorage
        saveCustomerInfo(formData.name, formData.phone);

        // API 응답의 예약번호를 사용하여 예약 결과 저장
        setReservation(
          items,
          formData,
          totalAmount,
          response.order.orderNumber,
          pickupDateTime.toISOString(),
          response.order.publicToken
        );

        // 장바구니 및 폼 데이터 클리어
        clearCart();
        resetReservation();
        clearPickupDateTime();

        // 예약 완료 페이지로 이동
        navigate('/confirm', { replace: true });
      } else {
        throw new Error('예약 신청에 실패했습니다.');
      }
    } catch (error) {
      const priceChanges = getPriceChangesFromError(error);
      if (priceChanges && priceChanges.length > 0) {
        setIdle();
        priceChangeDialogRef.current?.show(priceChanges);
        return;
      }

      const errorMessage = error instanceof Error ? error.message : '예약 신청 중 오류가 발생했습니다.';
      setError(errorMessage);
      console.error(errorMessage);
      toast.error(errorMessage);
    }
  };

  // 예약 버튼 활성화 조건 체크
  const isReservationEnabled =
    !isSubmitting() &&
    items.length > 0 && // 장바구니에 상품이 있어야 함
    isFormValid() && // 필수 폼 필드가 채워져야 함
    areAllTermsChecked(); // 모든 유의사항이 체크되어야 함

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <CartHeader />

      {/* 에러 메시지 표시 */}
      {error && (
        <div className="mx-5 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="text-red-600 font-semibold text-sm">⚠️ 오류</div>
            <div className="flex-1">
              <p className="text-red-700 text-sm">{error.message}</p>
              <button onClick={clearError} className="mt-2 text-red-600 text-xs underline">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 담은 상품 */}
      <CartItemList cartItems={items} getMaxQuantity={getStockAwareLimit} onUpdateQuantity={handleUpdateQuantity} />

      {/* 예약자 정보 */}
      <ReservationForm />

      {/* 유의사항 */}
      <TermsChecklist />

      {/* 예약 신청하기 버튼 */}
      <ReservationButton onReservation={handleReservation} disabled={!isReservationEnabled} loading={isSubmitting()} />

      {/* 삭제 확인 모달 */}
      <DeleteConfirmDialog ref={deleteConfirmDialogRef} onRemoveItem={removeItem} />

      {/* 가격 변경 알림 모달 */}
      <PriceChangeDialog ref={priceChangeDialogRef} onConfirm={applyPriceUpdates} />

      {/* 하단 여백 */}
      <div className="h-[120px]" />
    </div>
  );
}
